// src/ConsumerApp/Screens/Account/ProfileContent.js

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { theme } from '../../../assets/theme/theme';
import { useToast } from '../../context/ToastContext';

// Icons
import PencilSimpleLine from '../../../assets/icons/PencilSimpleLine1.svg';
import SealCheck from '../../../assets/icons/profileStatus/SealCheck.svg';
import ShieldCheck from '../../../assets/icons/profileStatus/ShieldCheck.svg';
import X from '../../../assets/icons/X.svg';
import ArrowLeft from '../../../assets/icons/ArrowLeft.svg';

// API Hooks
import {
  useGetProfileDetails,
  useUpdateProfileDetails,
  useDeleteCustomerAccount,
} from '../../api/profile/profile.hooks';

export default function ProfileContent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const storedUser = useMemo(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  const customerId = storedUser?._id || storedUser?.id;
  const token = storedUser?.token || sessionStorage.getItem('@auth_token') || localStorage.getItem('@auth_token');

  const { data: profileData, isLoading: loading } = useGetProfileDetails(customerId, token);
  const { mutate: updateProfile, isPending: isSaving } = useUpdateProfileDetails(token);
  const { mutate: deleteAccount, isPending: isDeletingAccount } = useDeleteCustomerAccount(token);

  const [isPersonalEditing, setIsPersonalEditing] = useState(false);
  const [isBusinessEditing, setIsBusinessEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');

  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [typeOfFirm, setTypeOfFirm] = useState('');
  const [gstin, setGstin] = useState('');
  const [pan, setPan] = useState('');
  const [association, setAssociation] = useState('');

  useEffect(() => {
    if (profileData) {
      setName(profileData.customer_name || profileData.name || '');
      setMobile(profileData.mobile_number || profileData.phone || '');
      setEmail(profileData.email || '');
      setCompanyName(profileData.company_name || '');
      setTypeOfFirm(profileData.type_of_firm || '');
      setGstin(profileData.gstin ? profileData.gstin.trim() : '');
      setPan(profileData.pan || '');
      setAssociation(profileData.association || '');

      setIsPersonalEditing(false);
      setIsBusinessEditing(false);
    }
  }, [profileData]);

  const isPhoneVerified = Boolean(profileData?.is_mobile_verified ?? true);
  const isEmailVerified = Boolean(profileData?.is_email_verified || (profileData?.email && profileData?.email.includes('@')));
  const isGstinVerified = Boolean(profileData?.gstin_verified || (profileData?.gstin && profileData?.gstin.length === 15));

  const handleGstinChange = (e) => {
    const val = e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 15).toUpperCase();
    setGstin(val);

    if (val.length >= 12) {
      setPan(val.substring(2, 12));
    }
  };

  const isFormDirty = useMemo(() => {
    if (!profileData) return false;
    return (
      name !== (profileData.customer_name || profileData.name || '') ||
      mobile !== (profileData.mobile_number || profileData.phone || '') ||
      email !== (profileData.email || '') ||
      companyName !== (profileData.company_name || '') ||
      typeOfFirm !== (profileData.type_of_firm || '') ||
      gstin !== (profileData.gstin ? profileData.gstin.trim() : '') ||
      pan !== (profileData.pan || '') ||
      association !== (profileData.association || '')
    );
  }, [name, mobile, email, companyName, typeOfFirm, gstin, pan, association, profileData]);

  const handleSave = () => {
    if (!name.trim()) {
      showToast('Name cannot be empty.', 'error');
      return;
    }

    const payload = {
      id: customerId,
      name: name.trim(),
      mobile: mobile.trim(),
      email: email.trim(),
      companyName: companyName.trim(),
      typeOfFirm: typeOfFirm.trim(),
      gstin: gstin.trim(),
      pan: pan.trim(),
      association: association.trim(),
    };

    updateProfile(payload, {
      onSuccess: () => {
        showToast('Profile updated successfully!', 'success');
        setIsPersonalEditing(false);
        setIsBusinessEditing(false);
      },
      onError: (err) => {
        showToast(err?.response?.data?.message || err?.message || 'Update failed.', 'error');
      },
    });
  };

  const handleDeleteConfirm = () => {
    deleteAccount(
      { customer_id: customerId, feedback: feedbackText.trim() },
      {
        onSuccess: () => {
          setShowDeleteModal(false);
          sessionStorage.clear();
          localStorage.clear();
          queryClient.clear();
          showToast('Account deleted successfully.', 'info');
          navigate('/consumer/HomeScreen', { replace: true });
        },
        onError: (err) => {
          showToast(err.message || 'Failed to delete account.', 'error');
        },
      }
    );
  };

  if (loading) {
    return (
      <div style={styles.loadingBox}>
        <span>Loading profile details...</span>
      </div>
    );
  }

  return (
    <div>
      <style>{`
        /* Standard 40x40 Circular Back Button */
        .bb-sub-circle-back-btn {
          width: 40px;
          height: 40px;
          border-radius: 999px;
          border: 1px solid #E8E0D0;
          background-color: #FFFFFF;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
          padding: 0;
          margin-right: 12px;
          outline: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
          transition: transform 0.12s ease, background-color 0.12s ease;
          box-sizing: border-box;
        }

        .bb-sub-circle-back-btn:active {
          transform: scale(0.92);
          background-color: #F8FAFC;
        }

        .bb-sub-circle-back-btn img {
          width: 20px;
          height: 20px;
          display: block;
        }

        .bb-sub-mobile-header {
          display: none;
        }

        .bb-sub-desktop-header {
          display: flex;
        }

        @media (max-width: 900px) {
          .bb-sub-desktop-header {
            display: none !important;
          }

          .bb-sub-mobile-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            height: 56px;
            padding: 0 16px;
            background-color: #FFFFFF;
            border-bottom: 1px solid #F1F5F9;
            box-sizing: border-box;
            position: sticky;
            top: 0;
            z-index: 1000;
          }

          .bb-sub-mobile-content {
            padding: 14px 16px 36px 16px !important;
          }
        }
      `}</style>

      {/* Mobile Edge-to-Edge Bar */}
      <div className="bb-sub-mobile-header">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <button
            type="button"
            className="bb-sub-circle-back-btn"
            onClick={() => navigate('/consumer/account', { replace: true })}
            aria-label="Back to Account"
          >
            <img src={ArrowLeft} alt="Back" />
          </button>
          <h2 style={{
            fontFamily: theme.typography.headingLG.fontFamily,
            fontSize: 18,
            fontWeight: '800',
            color: theme.colors.charcoal,
            margin: 0,
          }}>
            My Profile
          </h2>
        </div>
      </div>

      <div style={styles.container} className="bb-sub-mobile-content">
        {/* Desktop Header */}
        <div style={styles.pageHeader} className="bb-sub-desktop-header">
          <div>
            <h2 style={styles.headerTitle}>My Profile</h2>
            <span style={styles.headerSubtitle}>Manage your account information and preferences</span>
          </div>
        </div>

        {/* Status Badges */}
        <div style={styles.badgesRow}>
          <div style={{ ...styles.badge, ...(isPhoneVerified ? styles.successBadge : styles.warningBadge) }}>
            <img src={isPhoneVerified ? SealCheck : ShieldCheck} alt="phone" style={{ width: 13, height: 13 }} />
            <span>{isPhoneVerified ? 'Phone verified' : 'Phone unverified'}</span>
          </div>

          <div style={{ ...styles.badge, ...(isEmailVerified ? styles.successBadge : styles.neutralBadge) }}>
            <img src={isEmailVerified ? SealCheck : ShieldCheck} alt="email" style={{ width: 13, height: 13 }} />
            <span>{isEmailVerified ? 'Email verified' : 'Email pending'}</span>
          </div>

          <div style={{ ...styles.badge, ...(isGstinVerified ? styles.successBadge : styles.warningBadge) }}>
            <img src={isGstinVerified ? SealCheck : ShieldCheck} alt="gstin" style={{ width: 13, height: 13 }} />
            <span>{isGstinVerified ? 'GSTIN verified' : 'GSTIN pending'}</span>
          </div>
        </div>

        {/* Section 1: Personal Details */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.sectionTitle}>Personal Details</h3>
            <button
              type="button"
              style={styles.editToggleBtn}
              onClick={() => setIsPersonalEditing(!isPersonalEditing)}
            >
              {!isPersonalEditing && <img src={PencilSimpleLine} alt="edit" style={{ width: 13, height: 13 }} />}
              <span>{isPersonalEditing ? 'Cancel' : 'Edit'}</span>
            </button>
          </div>

          {isPersonalEditing ? (
            <div style={styles.formStack}>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Full Name</label>
                <input
                  type="text"
                  style={styles.input}
                  value={name}
                  maxLength={60}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter full name"
                />
              </div>

              <div style={styles.twoColRow}>
                <div style={{ ...styles.fieldGroup, flex: 1 }}>
                  <label style={styles.label}>Mobile Number</label>
                  <input
                    type="tel"
                    style={styles.input}
                    value={mobile}
                    maxLength={10}
                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="Enter mobile number"
                  />
                </div>

                <div style={{ ...styles.fieldGroup, flex: 1 }}>
                  <label style={styles.label}>Email Address</label>
                  <input
                    type="email"
                    style={styles.input}
                    value={email}
                    maxLength={80}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter email address"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div style={styles.readOnlyGrid}>
              <div style={styles.displayField}>
                <span style={styles.displayLabel}>Full Name</span>
                <span style={styles.displayValue}>{name || 'Not provided'}</span>
              </div>
              <div style={styles.displayField}>
                <span style={styles.displayLabel}>Mobile Number</span>
                <span style={styles.displayValue}>{mobile || 'Not provided'}</span>
              </div>
              <div style={styles.displayField}>
                <span style={styles.displayLabel}>Email</span>
                <span style={styles.displayValue}>{email || 'Not provided'}</span>
              </div>
            </div>
          )}
        </div>

        {/* Section 2: Business Details */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.sectionTitle}>Business Details</h3>
            <button
              type="button"
              style={styles.editToggleBtn}
              onClick={() => setIsBusinessEditing(!isBusinessEditing)}
            >
              {!isBusinessEditing && <img src={PencilSimpleLine} alt="edit" style={{ width: 13, height: 13 }} />}
              <span>{isBusinessEditing ? 'Cancel' : 'Edit'}</span>
            </button>
          </div>

          {isBusinessEditing ? (
            <div style={styles.formStack}>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Company Name</label>
                <input
                  type="text"
                  style={styles.input}
                  value={companyName}
                  maxLength={80}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="e.g. Greenfield Builders Pvt Ltd"
                />
              </div>

              <div style={styles.twoColRow}>
                <div style={{ ...styles.fieldGroup, flex: 1 }}>
                  <label style={styles.label}>GSTIN</label>
                  <input
                    type="text"
                    style={{ ...styles.input, textTransform: 'uppercase' }}
                    value={gstin}
                    maxLength={15}
                    onChange={handleGstinChange}
                    placeholder="22AAAAA0000A1Z5"
                  />
                </div>

                <div style={{ ...styles.fieldGroup, flex: 1 }}>
                  <label style={styles.label}>PAN</label>
                  <input
                    type="text"
                    style={{ ...styles.input, textTransform: 'uppercase' }}
                    value={pan}
                    maxLength={10}
                    onChange={(e) => setPan(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 10).toUpperCase())}
                    placeholder="ABCDE1234F"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div style={styles.readOnlyGrid}>
              <div style={styles.displayField}>
                <span style={styles.displayLabel}>Company Name</span>
                <span style={styles.displayValue}>{companyName || 'Not provided'}</span>
              </div>
              <div style={styles.displayField}>
                <span style={styles.displayLabel}>GSTIN</span>
                <span style={styles.displayValue}>{gstin || 'Not provided'}</span>
              </div>
              <div style={styles.displayField}>
                <span style={styles.displayLabel}>PAN</span>
                <span style={styles.displayValue}>{pan || 'Not provided'}</span>
              </div>
            </div>
          )}
        </div>

        {/* Save Button */}
        <div style={styles.saveActionRow}>
          <button
            type="button"
            style={{
              ...styles.saveBtn,
              opacity: !isFormDirty || isSaving ? 0.5 : 1,
              cursor: !isFormDirty || isSaving ? 'not-allowed' : 'pointer',
            }}
            onClick={handleSave}
            disabled={!isFormDirty || isSaving}
          >
            {isSaving ? 'Saving changes...' : 'Save changes'}
          </button>
        </div>

        {/* Danger Zone */}
        <div style={styles.dangerZone}>
          <button
            type="button"
            style={styles.deleteAccountLink}
            onClick={() => setShowDeleteModal(true)}
          >
            Delete Account
          </button>
          <span style={styles.deleteSubtext}>
            Permanently remove your profile, open orders, and billing records
          </span>
        </div>

        {/* Delete Modal */}
        {showDeleteModal && (
          <div style={styles.modalOverlay} onClick={() => !isDeletingAccount && setShowDeleteModal(false)}>
            <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <h4 style={styles.modalTitle}>Delete Procurement Account</h4>
                <button
                  type="button"
                  style={styles.modalCloseBtn}
                  onClick={() => setShowDeleteModal(false)}
                >
                  <img src={X} alt="close" style={{ width: 14, height: 14 }} />
                </button>
              </div>

              <p style={styles.modalWarning}>
                Deleting your account is permanent. You will immediately lose access to open quotes,
                material orders, RFQs, and GST billing records.
              </p>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>Anything we could have done better? (Optional)</label>
                <textarea
                  style={styles.textarea}
                  rows={3}
                  placeholder="Share your thoughts with us..."
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  maxLength={300}
                  disabled={isDeletingAccount}
                />
                <span style={styles.charCount}>{feedbackText.length}/300</span>
              </div>

              <div style={styles.modalActions}>
                <button
                  type="button"
                  style={styles.modalCancelBtn}
                  disabled={isDeletingAccount}
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  style={styles.modalDeleteBtn}
                  disabled={isDeletingAccount}
                  onClick={handleDeleteConfirm}
                >
                  {isDeletingAccount ? 'Deleting...' : 'Delete Account'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    width: '100%',
  },
  loadingBox: {
    padding: 60,
    textAlign: 'center',
    color: theme.colors.concrete,
    fontSize: 14,
    fontFamily: theme.typography.bodyMD.fontFamily,
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '1px solid #E2E8F0',
    paddingBottom: 16,
  },
  headerTitle: {
    ...theme.typography.headingLG,
    margin: 0,
    fontSize: 22,
    fontWeight: '800',
    color: theme.colors.charcoal,
  },
  headerSubtitle: {
    ...theme.typography.bodySM,
    fontSize: 13,
    color: theme.colors.concrete,
    marginTop: 4,
    display: 'block',
  },
  badgesRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  badge: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    padding: '4px 10px',
    borderRadius: 999,
    fontFamily: theme.typography.bodySM.fontFamily,
    fontSize: 11.5,
    fontWeight: '600',
  },
  successBadge: {
    backgroundColor: '#E8F5EE',
    color: '#1E8449',
  },
  warningBadge: {
    backgroundColor: '#FFF8EB',
    color: '#C47A00',
  },
  neutralBadge: {
    backgroundColor: '#F1F5F9',
    color: '#64748B',
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px solid #CBD5E1',
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sectionTitle: {
    ...theme.typography.headingMD,
    margin: 0,
    fontSize: 15,
    fontWeight: '700',
    color: theme.colors.charcoal,
  },
  editToggleBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    background: 'none',
    border: 'none',
    color: theme.colors.primaryRed || '#C7061F',
    fontFamily: theme.typography.bodySM.fontFamily,
    fontSize: 13,
    fontWeight: '600',
    cursor: 'pointer',
    padding: 0,
  },
  formStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  twoColRow: {
    display: 'flex',
    gap: 16,
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontFamily: theme.typography.bodySM.fontFamily,
    fontSize: 12.5,
    fontWeight: '600',
    color: theme.colors.charcoal,
  },
  input: {
    height: 42,
    borderRadius: 8,
    border: '1px solid #CBD5E1',
    padding: '0 14px',
    fontFamily: theme.typography.bodyMD.fontFamily,
    fontSize: 13.5,
    color: theme.colors.charcoal,
    outline: 'none',
    boxSizing: 'border-box',
    width: '100%',
    backgroundColor: '#FFFFFF',
  },
  readOnlyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 16,
  },
  displayField: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  displayLabel: {
    fontFamily: theme.typography.bodySM.fontFamily,
    fontSize: 11.5,
    color: theme.colors.concrete,
  },
  displayValue: {
    fontFamily: theme.typography.bodyMD.fontFamily,
    fontSize: 13.5,
    fontWeight: '600',
    color: theme.colors.charcoal,
  },
  saveActionRow: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  saveBtn: {
    backgroundColor: theme.colors.primaryRed || '#C7061F',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 8,
    padding: '12px 28px',
    fontFamily: theme.typography.bodyMD.fontFamily,
    fontSize: 14,
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'opacity 0.2s ease',
  },
  dangerZone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
    paddingTop: 12,
  },
  deleteAccountLink: {
    background: 'none',
    border: 'none',
    color: '#DC2626',
    fontFamily: theme.typography.bodyMD.fontFamily,
    fontSize: 13.5,
    fontWeight: '700',
    cursor: 'pointer',
    padding: 0,
  },
  deleteSubtext: {
    fontFamily: theme.typography.bodySM.fontFamily,
    fontSize: 11.5,
    color: theme.colors.concrete,
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.65)',
    zIndex: 999999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(3px)',
    padding: 20,
  },
  modalCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    padding: '24px 28px',
    width: '100%',
    maxWidth: 440,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.25)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalTitle: {
    ...theme.typography.headingMD,
    margin: 0,
    fontSize: 17,
    fontWeight: '700',
    color: theme.colors.charcoal,
  },
  modalCloseBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
  },
  modalWarning: {
    margin: 0,
    fontFamily: theme.typography.bodySM.fontFamily,
    fontSize: 13,
    color: '#636C78',
    lineHeight: '19px',
  },
  textarea: {
    width: '100%',
    borderRadius: 8,
    border: '1px solid #CBD5E1',
    padding: '10px 12px',
    fontFamily: theme.typography.bodySM.fontFamily,
    fontSize: 13,
    color: theme.colors.charcoal,
    outline: 'none',
    boxSizing: 'border-box',
    resize: 'none',
  },
  charCount: {
    fontFamily: theme.typography.bodySM.fontFamily,
    fontSize: 11,
    color: theme.colors.concrete,
    alignSelf: 'flex-end',
    marginTop: 2,
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 4,
  },
  modalCancelBtn: {
    padding: '10px 18px',
    borderRadius: 8,
    border: '1px solid #CBD5E1',
    backgroundColor: '#FFFFFF',
    color: theme.colors.charcoal,
    fontFamily: theme.typography.bodySM.fontFamily,
    fontSize: 13,
    fontWeight: '600',
    cursor: 'pointer',
  },
  modalDeleteBtn: {
    padding: '10px 18px',
    borderRadius: 8,
    border: 'none',
    backgroundColor: '#DC2626',
    color: '#FFFFFF',
    fontFamily: theme.typography.bodySM.fontFamily,
    fontSize: 13,
    fontWeight: '700',
    cursor: 'pointer',
  },
};